/**
 * usePartnerAccess — server-validated hook for partner portal visibility.
 *
 * Calls `GET /api/partner/me` which:
 *   - 200 → signed-in user has an ACTIVE partner membership AND the partner
 *           itself is ACTIVE. Returns `{ hasAccess: true, partner, membership }`.
 *   - 401 → not signed in. hasAccess=false (silent).
 *   - 403 → not a partner, or membership revoked/suspended, or partner
 *           suspended/archived. hasAccess=false (silent).
 *   - other → hasAccess=false (silent). Portal UX uses its own error state.
 *
 * The result is ALWAYS re-checked when the auth identity changes so that:
 *   - Logging in surfaces the Partner Portal nav item for eligible accounts.
 *   - Logging out hides it immediately.
 *   - Admins who revoke a membership propagate on the next auth refresh.
 *
 * We never expose partner data from this hook beyond what the nav needs
 * (label / status). The portal itself re-fetches server-side everywhere.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { getToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const usePartnerAccess = () => {
  const { user } = useAuth();
  const [state, setState] = useState({
    loading: true,
    hasAccess: false,
    partner: null,
    membership: null,
  });

  const check = useCallback(async () => {
    // Not signed in — never show partner nav.
    if (!user || !getToken()) {
      setState({ loading: false, hasAccess: false, partner: null, membership: null });
      return;
    }
    // Guard against stale in-flight responses setting state after a logout
    // or an account switch. Returns true when caller should ignore the result.
    const controller = new AbortController();
    try {
      const r = await axios.get(`${API}/partner/me`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      setState({
        loading: false,
        hasAccess: true,
        partner: r.data?.partner || null,
        membership: r.data?.membership || null,
      });
    } catch {
      if (controller.signal.aborted) return;
      // 401 / 403 / network — hide silently.
      setState({ loading: false, hasAccess: false, partner: null, membership: null });
    }
    return controller;
  }, [user]);

  // Re-check whenever the authenticated identity changes.
  const userKey = user ? (user.user_id || user.id || user.email || "u") : String(user);
  useEffect(() => {
    let cancelled = false;
    let controller;
    (async () => {
      controller = await check();
      if (cancelled && controller) controller.abort();
    })();
    return () => {
      cancelled = true;
      if (controller) controller.abort();
    };
  }, [check, userKey]);

  return state;
};
